import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Input, Form, Radio, Checkbox, Typography } from "antd";
import styled from "styled-components";
import tokens from "../../theme/tokens";

const { TextArea } = Input;
const { Text } = Typography;

/**
 * Add/Edit modals for the Notification Letter exemplar library
 * (Settings > AI Categorization > Notification Letters).
 *
 * Same admin curation method as the gold standards (AddGoldStandardModal /
 * EditGoldStandardModal): pick an existing DMS document, record the
 * ground truth, optional notes. The one domain difference is the label
 * being taught — instead of a category, the admin records what was
 * physically attached behind the letter in that scan, because that is
 * what drives deadline calendaring.
 */

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const Hint = styled.div`
  margin-top: 4px;
  color: ${tokens.text.tertiary};
  font-size: 12px;
  font-family: ${tokens.typography.fontFamily};
`;

const SECTION_OPTIONS = [
  { value: "rating_decision", label: "Rating Decision" },
  { value: "codesheet", label: "Codesheet" },
];

/**
 * Shared "what was attached" control. `value` is the enclosed_sections
 * array; [] means a standalone letter (counter-example).
 */
export const EnclosureField = ({ value, onChange, disabled = false }) => {
  const isPacket = value.length > 0;
  return (
    <Form.Item label="What was attached behind this letter?" required>
      <Radio.Group
        value={isPacket ? "packet" : "standalone"}
        onChange={(e) =>
          onChange(e.target.value === "packet" ? ["rating_decision"] : [])
        }
        disabled={disabled}
        data-testid="notification-letter-kind"
      >
        <Radio value="packet" data-testid="notification-letter-kind-packet">
          A decision was attached — teaches the system to calendar a deadline
        </Radio>
        <Radio
          value="standalone"
          data-testid="notification-letter-kind-standalone"
        >
          Nothing attached — a look-alike letter that must NOT be calendared
        </Radio>
      </Radio.Group>
      {isPacket && (
        <div style={{ marginTop: 8, marginLeft: 24 }}>
          <Checkbox.Group
            options={SECTION_OPTIONS}
            value={value}
            onChange={(sections) =>
              // A packet must attach at least one section; refuse to
              // uncheck the last box rather than silently flipping the
              // row into a counter-example.
              onChange(sections.length > 0 ? sections : value)
            }
            disabled={disabled}
            data-testid="notification-letter-sections"
          />
        </div>
      )}
    </Form.Item>
  );
};

EnclosureField.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export const AddNotificationLetterModal = ({
  open,
  saving = false,
  success = false,
  error = "",
  onCancel,
  onSubmit,
  // In-context mode ("add this doc from my inbox"): the caller already
  // knows which document, so the ID field never renders. Matthew's
  // 2026-08-12 review of the ID-typing flow: "this is terrible" — the
  // typed-ID path stays only as the Settings-page fallback.
  presetDocument = null,
}) => {
  const [documentId, setDocumentId] = useState("");
  const [sections, setSections] = useState(["rating_decision"]);
  const [notes, setNotes] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDocumentId("");
    setSections(["rating_decision"]);
    setNotes("");
    setTouched(false);
  }, [open]);

  const effectiveDocumentId = presetDocument?.id || documentId.trim();
  const docIdValid = UUID_RE.test(effectiveDocumentId);
  const canSubmit = docIdValid && !saving && !success;

  const handleSubmit = () => {
    if (!canSubmit) {
      setTouched(true);
      return;
    }
    onSubmit({
      documentId: effectiveDocumentId,
      enclosedSections: sections,
      notes: notes.trim() ? notes.trim() : undefined,
    });
  };

  return (
    <Modal
      open={open}
      title="Add notification letter"
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={saving ? "Saving…" : success ? "Saved" : "Add to library"}
      cancelText="Cancel"
      okButtonProps={{
        disabled: !canSubmit,
        "data-testid": "add-notification-letter-submit",
      }}
      cancelButtonProps={{ disabled: saving }}
      maskClosable={!saving}
      destroyOnHidden
      data-testid="add-notification-letter-modal"
    >
      <Form layout="vertical">
        {presetDocument ? (
          <Form.Item label="Letter">
            <div
              style={{
                padding: "6px 10px",
                borderRadius: 8,
                background: tokens.surface.page,
                border: `1px solid ${tokens.border.light}`,
                color: tokens.text.primary,
                fontFamily: tokens.typography.fontFamily,
                fontSize: 13,
                wordBreak: "break-word",
              }}
              data-testid="add-notification-letter-preset-name"
            >
              {presetDocument.filename || "Selected document"}
            </div>
          </Form.Item>
        ) : (
          <Form.Item
            label="Document ID"
            required
            validateStatus={touched && !docIdValid ? "error" : ""}
            help={
              touched && !docIdValid
                ? "Enter the document ID (copy it from the document URL)."
                : null
            }
          >
            <Input
              value={documentId}
              onChange={(event) => {
                setDocumentId(event.target.value);
                setTouched(true);
              }}
              disabled={saving || success}
              placeholder="e.g. af8dc6d4-77c1-4f6b-95b5-a2e40d2ef0d4"
              data-testid="add-notification-letter-doc-id"
            />
            <Hint>
              The letter must already exist in DMS. We&rsquo;ll pull its
              filename, summary, and thumbnail from there.
            </Hint>
          </Form.Item>
        )}

        <EnclosureField
          value={sections}
          onChange={setSections}
          disabled={saving || success}
        />

        <Form.Item
          label="Internal notes (optional)"
          tooltip="Visible to admins only. Use this to capture why this letter is a good example."
        >
          <TextArea
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={saving || success}
            placeholder="Optional context for other admins."
            data-testid="add-notification-letter-notes"
          />
        </Form.Item>

        {error && (
          <Text
            type="danger"
            role="alert"
            data-testid="add-notification-letter-error"
          >
            {error}
          </Text>
        )}
      </Form>
    </Modal>
  );
};

AddNotificationLetterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  saving: PropTypes.bool,
  success: PropTypes.bool,
  error: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  presetDocument: PropTypes.shape({
    id: PropTypes.string,
    filename: PropTypes.string,
  }),
};

export const EditNotificationLetterModal = ({
  open,
  row,
  saving = false,
  error = "",
  onCancel,
  onSubmit,
}) => {
  const [sections, setSections] = useState([]);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    setSections(row?.enclosed_sections || []);
    setNotes(row?.notes || "");
  }, [open, row]);

  const handleSubmit = () => {
    if (saving) return;
    onSubmit({ enclosed_sections: sections, notes: notes.trim() });
  };

  return (
    <Modal
      open={open}
      title="Edit notification letter"
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={saving ? "Saving…" : "Save"}
      cancelText="Cancel"
      okButtonProps={{
        disabled: saving,
        "data-testid": "edit-notification-letter-submit",
      }}
      cancelButtonProps={{ disabled: saving }}
      maskClosable={!saving}
      destroyOnHidden
      data-testid="edit-notification-letter-modal"
    >
      <Form layout="vertical">
        <Form.Item label="Letter">
          <div
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              background: tokens.surface.page,
              border: `1px solid ${tokens.border.light}`,
              color: tokens.text.primary,
              fontFamily: tokens.typography.fontFamily,
              fontSize: 13,
              wordBreak: "break-word",
            }}
          >
            {row?.filename || "—"}
          </div>
        </Form.Item>

        <EnclosureField value={sections} onChange={setSections} disabled={saving} />

        <Form.Item label="Internal notes">
          <TextArea
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={saving}
            placeholder="Optional context for other admins."
            data-testid="edit-notification-letter-notes"
          />
        </Form.Item>

        {error && (
          <Text
            type="danger"
            role="alert"
            data-testid="edit-notification-letter-error"
          >
            {error}
          </Text>
        )}
      </Form>
    </Modal>
  );
};

EditNotificationLetterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  row: PropTypes.object,
  saving: PropTypes.bool,
  error: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
